import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Store, Plus, PhoneOff, Search, Loader2, MapPin, Phone, Star, Check,
} from "lucide-react";

const MODES = [
  { key: "existing", label: "Magasin existant", Icon: Store },
  { key: "new", label: "Nouveau magasin", Icon: Plus },
  { key: "invalid", label: "Numéro erroné", Icon: PhoneOff },
];

// Modal de rattachement d'un appel « orphelin » (numéro sans magasin) à un magasin.
// Utilisé après un appel (widget) — mode `forced` — et sur les appels orphelins
// existants (page Appels). N'écrase jamais un contact : ajoute en secondaire, ou
// bascule en principal (l'ancien principal étant conservé en secondaire).
export function ContactLinkDialog({ call, open, onClose, onLinked, forced = false }) {
  const [mode, setMode] = useState("existing");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [spot, setSpot] = useState(null);
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [fonction, setFonction] = useState("");
  const [asPrincipal, setAsPrincipal] = useState(false);
  const [nf, setNf] = useState({ enseigne: "", ville: "", rue: "", dept: "", type: "supermarche", nom: "", prenom: "", fonction: "" });

  const phone = call?.to_number || "";

  useEffect(() => {
    if (!open) return;
    setMode("existing"); setSearch(""); setResults([]); setSpot(null);
    setNom(""); setPrenom(""); setFonction(""); setAsPrincipal(false);
    setNf({ enseigne: "", ville: "", rue: "", dept: "", type: "supermarche", nom: "", prenom: "", fonction: "" });
  }, [open, call?.id]);

  useEffect(() => {
    if (!open || mode !== "existing") return undefined;
    const q = search.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    let cancel = false; setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/spots", { params: { search: q, limit: 8 } });
        if (!cancel) setResults(data || []);
      } catch (_) { if (!cancel) setResults([]); }
      finally { if (!cancel) setSearching(false); }
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [search, mode, open]);

  const setNfField = (k) => (e) => setNf((p) => ({ ...p, [k]: e.target.value }));

  const doLinkExisting = async () => {
    if (!spot) { toast.error("Sélectionnez un magasin."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/calls/${call.id}/link`, {
        mode: "existing", spot_id: spot.id, add_contact: true, as_principal: asPrincipal,
        nom: nom.trim() || undefined, prenom: prenom.trim() || undefined, fonction: fonction.trim() || undefined,
      });
      toast.success(`Contact rattaché à ${data.spot_name}`);
      onLinked?.(data); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rattachement impossible"); }
    finally { setSaving(false); }
  };

  const doCreateNew = async () => {
    if (!nf.enseigne.trim() || !nf.ville.trim()) { toast.error("Enseigne et ville obligatoires."); return; }
    setSaving(true);
    try {
      const { data: sp } = await api.post("/spots", {
        enseigne: nf.enseigne.trim(), type: nf.type, ville: nf.ville.trim(),
        rue: nf.rue.trim() || undefined, dept: nf.dept.trim() || undefined,
        status: "jamais_appele", flux: {},
        contact_principal: {
          nom: nf.nom.trim() || undefined, prenom: nf.prenom.trim() || undefined,
          fonction: nf.fonction.trim() || undefined, telephone: phone,
        },
      });
      const { data } = await api.post(`/calls/${call.id}/link`, { mode: "existing", spot_id: sp.id, add_contact: false });
      toast.success(`Magasin créé et rattaché : ${sp.normalized_name}`);
      onLinked?.(data); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
    finally { setSaving(false); }
  };

  const doInvalid = async () => {
    setSaving(true);
    try {
      await api.post(`/calls/${call.id}/link`, { mode: "invalid" });
      toast.success("Numéro marqué comme erroné");
      onLinked?.({ contact_status: "invalid" }); onClose?.();
    } catch (_) { toast.error("Action impossible"); }
    finally { setSaving(false); }
  };

  const principal = spot?.contact_principal || null;
  const principalLabel = principal
    ? [principal.prenom, principal.nom].filter(Boolean).join(" ") || principal.fonction || principal.telephone || "—"
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !forced) onClose?.(); }}>
      <DialogContent
        className={`max-w-lg ${forced ? "[&>button]:hidden" : ""}`}
        onInteractOutside={(e) => forced && e.preventDefault()}
        onEscapeKeyDown={(e) => forced && e.preventDefault()}
        data-testid="contact-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-[var(--shariti-orange)]" /> Rattacher ce numéro
          </DialogTitle>
          <DialogDescription>
            {forced
              ? "Ce numéro n'est associé à aucun magasin. Rattachez-le pour ne perdre aucun contact."
              : "Associez cet appel à un magasin pour ne perdre aucune information."}
            {phone && <span className="ml-1 font-semibold text-ink">{phone}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-1.5" data-testid="link-mode-tabs">
          {MODES.map((m) => {
            const active = mode === m.key;
            const Icon = m.Icon;
            return (
              <button key={m.key} type="button" onClick={() => setMode(m.key)}
                data-testid={`link-mode-${m.key}`}
                className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-semibold transition-colors ${
                  active
                    ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)]"
                    : "border-[var(--border-soft)] bg-white text-ink hover:bg-[var(--surface-2)]"}`}>
                <Icon className="h-4 w-4" /> {m.label}
              </button>
            );
          })}
        </div>

        {mode === "existing" && (
          <div className="space-y-3">
            {!spot ? (
              <div className="space-y-2">
                <Label className="text-xs">Rechercher le magasin</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-ink" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} autoFocus
                    placeholder="Nom du magasin, ville…" className="pl-8" data-testid="link-spot-search" />
                </div>
                <div className="max-h-52 space-y-1 overflow-y-auto">
                  {searching && <div className="flex items-center gap-2 px-1 py-2 text-xs text-muted-ink"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Recherche…</div>}
                  {!searching && search.trim().length >= 2 && results.length === 0 && (
                    <div className="px-1 py-2 text-xs text-muted-ink">Aucun magasin trouvé. Essayez « Nouveau magasin ».</div>
                  )}
                  {results.map((s) => (
                    <button key={s.id} type="button" onClick={() => setSpot(s)}
                      data-testid={`link-spot-option-${s.id}`}
                      className="flex w-full items-start gap-2 rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-2 text-left transition-colors hover:bg-[var(--surface-2)]">
                      <Store className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                      <div className="min-w-0">
                        <div className="break-words text-sm font-semibold text-ink">{s.normalized_name}</div>
                        <div className="flex items-center gap-1 text-[11px] text-muted-ink"><MapPin className="h-3 w-3" />{s.ville || "—"}{s.dept ? ` (${s.dept})` : ""}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2 rounded-lg border border-[rgba(238,114,46,0.35)] bg-[rgba(238,114,46,0.06)] px-3 py-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-bold text-ink"><Store className="h-4 w-4 text-[var(--shariti-orange)]" />{spot.normalized_name}</div>
                    <div className="text-[11px] text-muted-ink">Contact principal actuel : <span className="font-semibold">{principalLabel || "aucun"}</span></div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSpot(null)} data-testid="link-change-spot">Changer</Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Prénom</Label><Input value={prenom} onChange={(e) => setPrenom(e.target.value)} className="mt-1 h-9" data-testid="link-contact-prenom" /></div>
                  <div><Label className="text-xs">Nom</Label><Input value={nom} onChange={(e) => setNom(e.target.value)} className="mt-1 h-9" data-testid="link-contact-nom" /></div>
                </div>
                <div><Label className="text-xs">Fonction</Label><Input value={fonction} onChange={(e) => setFonction(e.target.value)} placeholder="Directeur, responsable…" className="mt-1 h-9" data-testid="link-contact-fonction" /></div>
                <div className="rounded-lg bg-[var(--surface-2)] px-3 py-2 text-[11px] text-muted-ink">
                  <Phone className="mr-1 inline h-3 w-3" /> Téléphone ajouté : <span className="font-semibold text-ink">{phone}</span>
                </div>
                <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-[var(--border-soft)] px-3 py-2" data-testid="link-as-principal-row">
                  <Checkbox checked={asPrincipal} onCheckedChange={(v) => setAsPrincipal(!!v)} data-testid="link-as-principal" className="mt-0.5" />
                  <span className="text-xs">
                    <span className="flex items-center gap-1 font-semibold text-ink"><Star className="h-3.5 w-3.5 text-[var(--shariti-orange)]" /> Définir comme contact principal</span>
                    <span className="text-muted-ink">L'ancien contact principal sera conservé en contact secondaire (aucune perte).</span>
                  </span>
                </label>
              </div>
            )}
          </div>
        )}

        {mode === "new" && (
          <div className="space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Enseigne *</Label><Input value={nf.enseigne} onChange={setNfField("enseigne")} placeholder="Franprix" className="mt-1 h-9" data-testid="link-new-enseigne" /></div>
              <div><Label className="text-xs">Ville *</Label><Input value={nf.ville} onChange={setNfField("ville")} placeholder="Paris" className="mt-1 h-9" data-testid="link-new-ville" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Rue</Label><Input value={nf.rue} onChange={setNfField("rue")} className="mt-1 h-9" data-testid="link-new-rue" /></div>
              <div><Label className="text-xs">Département</Label><Input value={nf.dept} onChange={setNfField("dept")} placeholder="75" className="mt-1 h-9" data-testid="link-new-dept" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Prénom contact</Label><Input value={nf.prenom} onChange={setNfField("prenom")} className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Nom contact</Label><Input value={nf.nom} onChange={setNfField("nom")} className="mt-1 h-9" /></div>
            </div>
            <div><Label className="text-xs">Fonction</Label><Input value={nf.fonction} onChange={setNfField("fonction")} className="mt-1 h-9" /></div>
            <div className="rounded-lg bg-[var(--surface-2)] px-3 py-2 text-[11px] text-muted-ink">
              <Phone className="mr-1 inline h-3 w-3" /> Ce numéro deviendra le contact principal : <span className="font-semibold text-ink">{phone}</span>
            </div>
          </div>
        )}

        {mode === "invalid" && (
          <div className="rounded-lg border border-[rgba(214,69,69,0.35)] bg-[rgba(214,69,69,0.05)] px-3 py-3 text-sm text-[#9B2226]" data-testid="link-invalid-note">
            <PhoneOff className="mb-1 inline h-4 w-4" /> Ce numéro sera marqué comme <strong>erroné / inexploitable</strong>. Il ne sera plus proposé au rattachement, mais l'appel reste conservé dans l'historique.
          </div>
        )}

        <DialogFooter className="gap-2">
          {!forced && <Button variant="ghost" onClick={onClose} data-testid="link-cancel">Annuler</Button>}
          {mode === "existing" && (
            <Button onClick={doLinkExisting} disabled={saving || !spot} data-testid="link-submit-existing"
              className="bg-[var(--shariti-orange)] hover:bg-[#E56622]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-1.5 h-4 w-4" /> Rattacher</>}
            </Button>
          )}
          {mode === "new" && (
            <Button onClick={doCreateNew} disabled={saving} data-testid="link-submit-new"
              className="bg-[var(--shariti-orange)] hover:bg-[#E56622]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1.5 h-4 w-4" /> Créer et rattacher</>}
            </Button>
          )}
          {mode === "invalid" && (
            <Button onClick={doInvalid} disabled={saving} variant="destructive" data-testid="link-submit-invalid">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PhoneOff className="mr-1.5 h-4 w-4" /> Marquer erroné</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ContactLinkDialog;
